import { useLocation } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  type DesktopHeaderEffect,
  transitionDesktopHeader,
} from "@/app/layouts/desktopHeaderMediator";
import { resolveHeaderMascot } from "@/app/layouts/headerMascot";
import { isHistoryPath } from "@/app/layouts/headerSearchVisibility";
import { openJukeboxWindow } from "@/app/layouts/jukeboxWindow";
import { BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import { useAnnouncementStatus } from "@/features/announce/hooks/useAnnouncementStatus";
import { useDesktopAnnouncementModal } from "@/features/announce/hooks/useDesktopAnnouncementModal";
import { useCurrentUrlSlug } from "@/shared/hooks/useCurrentUrlSlug";
import { useIsPcForced } from "@/shared/hooks/useMediaQuery";
import {
  createMediator,
  type EventNode,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";
import { buildMobileVersionUrl } from "@/shared/lib/forcePc";
import { resolveThreadBoardScope } from "@/shared/lib/threadBoardScope";
import {
  isThreadRoutePath,
  parseMixRouteThreadSlug,
} from "@/shared/lib/threadRoutePath";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

export interface DesktopHeaderPresentation {
  node: EventNode<TypedEvent>;
  usesPageToolbar: boolean;
  isThreadView: boolean;
  displayName: string;
  mascot: string;
  mobileVersionUrl: string | null;
  isJukeboxOpen: boolean;
  hasUnreadAnnouncements: boolean;
  isAnnouncementMetaPending: boolean;
}

export function useDesktopHeaderPresenter(): DesktopHeaderPresentation {
  const location = useLocation();
  const usesPageToolbar =
    location.pathname === "/" ||
    location.pathname === "/board" ||
    location.pathname === "/archive" ||
    location.pathname === "/search" ||
    isHistoryPath(location.pathname);
  const isThreadView = isThreadRoutePath(location.pathname);
  const displayName = isThreadView
    ? resolveThreadBoardScope(parseMixRouteThreadSlug(location.pathname))
        .displayName
    : BOARD_DISPLAY_NAME;
  const mascot = resolveHeaderMascot(useCurrentUrlSlug());
  const isPcForced = useIsPcForced();
  const { openAnnouncementModal } = useDesktopAnnouncementModal();
  const { hasUnreadAnnouncements, isAnnouncementMetaPending } =
    useAnnouncementStatus();
  const mediator = useMemo(
    () =>
      createMediator(false, transitionDesktopHeader, {
        runEffect(effect: DesktopHeaderEffect): void {
          if (effect === "open-jukebox")
            openJukeboxWindow(window.open.bind(window));
          else openAnnouncementModal();
        },
      }),
    [openAnnouncementModal],
  );
  useHierarchyLifetime(mediator);
  const isJukeboxOpen = useMediatorSnapshot(mediator);
  const node = useComponentNode((event) =>
    mediator.dispatch(event).status === "handled" ? "handled" : "unhandled",
  );
  return {
    node,
    usesPageToolbar,
    isThreadView,
    displayName,
    mascot,
    mobileVersionUrl: isPcForced
      ? buildMobileVersionUrl(window.location)
      : null,
    isJukeboxOpen,
    hasUnreadAnnouncements,
    isAnnouncementMetaPending,
  };
}
